import { makeAutoObservable, reaction } from "mobx"

import { authStore } from "@src/app/authStore"
import { wrapSingleton } from "@src/shared/classes/utils/wrapSingleton"
import { BaseTour } from "@src/widgets/onboarding/classes/BaseTour"
import { ProjectsStep } from "@src/widgets/onboarding/classes/tours/ApiIntegrationTour/steps/ProjectsStep"
import {
  ONBOARDING_STORAGE_KEY,
  ONBOARDING_TOURS_MAP,
} from "@src/widgets/onboarding/constants"
import {
  ONBOARDING_TOURS,
  OnboardingCashedState,
} from "@src/widgets/onboarding/types"

type Rules = Partial<
  Record<
    ONBOARDING_TOURS,
    {
      condition: boolean
      steps: Record<string, boolean>
    }
  >
>

export class Onboarding {
  private authStore = authStore

  private settings = [ONBOARDING_TOURS.MANDATORY_INITIAL_SETUP]
  constructor() {
    makeAutoObservable(this)

    const { merchant } = authStore
    const cached = this.getCachedData()
    const tryStart = [!merchant?.referral_link_id, !cached].every(Boolean)

    reaction(
      () => [this.rules, this.settings],
      (rules) => {
        this.tours.forEach((t) => {
          const rule = this.rules[t.name]

          if (rule) {
            t.setEnable(rule.condition)

            Object.entries(rule.steps).forEach(([s, v]) => {
              const find = t.steps.find((step) => step.name === s)

              if (find) {
                find.setEnable(v)
              }
            })
          }
        })
      },
    )
    if (tryStart) {
      this.next()
    }
  }

  private get completedTours(): BaseTour[] {
    return this.tours.filter((t) => t.isCompleted)
  }

  private getCachedData(): OnboardingCashedState | null {
    const cached = window.localStorage.getItem(ONBOARDING_STORAGE_KEY)

    if (!cached) {
      return null
    }

    return JSON.parse(cached) as OnboardingCashedState
  }

  private get rules(): Rules {
    const mecrch = authStore.merchant

    return {
      [ONBOARDING_TOURS.ACCOUNT_TOP_UP]: {
        condition: true,
        steps: {
          [`${ONBOARDING_TOURS.ACCOUNT_TOP_UP}_FiatBalanceTopUpStep`]:
            mecrch?.kyb_level === 2,
        },
      },

      [ONBOARDING_TOURS.ON_RAMP_WIDGET]: {
        condition: Boolean(mecrch?.is_widgets_enabled),
        steps: {},
      },
    }
  }

  private setCachedData() {
    const cached: OnboardingCashedState = {
      completed: this.tours
        .filter(({ isCompleted }) => isCompleted)
        .map(({ name }) => name),
      isClosed: false,
      settings: this.settings,
      step: this.activeTour?.activeStep?.name,
      tourInProgress: this.activeTour?.name,
    }

    window.localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(cached))
  }

  private get tours(): BaseTour[] {
    return [
      ONBOARDING_TOURS_MAP[ONBOARDING_TOURS.WELCOME],
      ...this.settings
        .slice()
        .sort((a, b) => a - b)
        .map((s) => ONBOARDING_TOURS_MAP[s])
        .filter((t) => t.enable),
    ]
  }

  back() {
    if (this.activeTour.isInitialStep) {
      return this.startSomeTour(-1)
    }

    return this.activeTour.prevStep()
  }

  next() {
    if (!this.activeTour.isStarted) {
      return this.startSomeTour(0)
    }

    if (this.activeTour.isLastStep) {
      return this.startSomeTour(1)
    }

    return this.activeTour.nextStep()
  }

  setSettings(settings: ONBOARDING_TOURS[]) {
    this.settings = settings
  }

  async startSomeTour(direction: -1 | 0 | 1) {
    const index = this.tours.indexOf(this.activeTour)

    if (index !== -1) {
      if (direction > 0) {
        this.activeTour.setComplete()
      }

      const t = this.tours[index + direction]

      if (t) {
        return t.start().then((_) => this.setCachedData())
      }
    }
  }

  get activeTour(): BaseTour {
    const started = this.tours.find((t) => t.isStarted)

    return started ?? (this.tours.at(0) as BaseTour)
  }

  get isProgress() {
    return Boolean(this.activeTour)
  }
}

export const onboardingStore = wrapSingleton(Onboarding)
